import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Checkbox } from '../components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import { Loader2, ArrowRight } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_charity-fashion-1/artifacts/hpjktkvw_260208%20Logo%20Galeries%20Yess4_zw.png";

const AccessPage = () => {
    const { verifyOTP, completeLogin } = useAuth();
    const navigate = useNavigate();
    
    const [step, setStep] = useState(1);
    const [phone, setPhone] = useState('');
    const [name, setName] = useState('');
    const [code, setCode] = useState('');
    const [loading, setLoading] = useState(false);
    const [houseRulesChecked, setHouseRulesChecked] = useState(false);
    const [termsChecked, setTermsChecked] = useState(false);
    const [tempToken, setTempToken] = useState(null);
    const [tempUser, setTempUser] = useState(null);
    const [showHouseRules, setShowHouseRules] = useState(false);
    const [houseRulesContent, setHouseRulesContent] = useState(null);
    const [houseRulesLoading, setHouseRulesLoading] = useState(false);

    useEffect(() => {
        if (showHouseRules && !houseRulesContent) {
            fetchHouseRules();
        }
    }, [showHouseRules]);

    const fetchHouseRules = async () => {
        setHouseRulesLoading(true);
        try {
            const response = await axios.get(`${API_URL}/api/content/house-rules`);
            setHouseRulesContent(response.data);
        } catch (error) {
            console.error('Failed to fetch house rules:', error);
        } finally {
            setHouseRulesLoading(false);
        }
    };

    const handleLogin = async (e) => {
        e.preventDefault();
        
        if (!phone || phone.length < 10) {
            toast.error('Please enter a valid phone number');
            return;
        }
        if (!name || name.trim().length < 1) {
            toast.error('Please enter your name');
            return;
        }
        if (!code || code.length < 4) {
            toast.error('Please enter your access code');
            return;
        }

        setLoading(true);
        try {
            const formattedPhone = phone.startsWith('+') ? phone : `+${phone}`;
            const result = await verifyOTP(formattedPhone, code.toUpperCase(), name.trim(), null);
            
            if (result.user?.consent_accepted === true) {
                toast.success('Welcome back to Galeries Yess!');
                navigate('/home');
            } else {
                setTempToken(result.token);
                setTempUser(result.user);
                setStep(2);
            }
        } catch (error) {
            const message = error.response?.data?.detail || 'Login failed';
            toast.error(message);
        } finally {
            setLoading(false);
        }
    };

    const handleAcceptConsent = async (e) => {
        e.preventDefault();
        if (!houseRulesChecked || !termsChecked) {
            toast.error('Please accept both to continue');
            return;
        }

        setLoading(true);
        try {
            const response = await axios.post(`${API_URL}/api/auth/consent`, {}, {
                headers: { Authorization: `Bearer ${tempToken}` }
            });
            
            completeLogin(tempToken, response.data);
            toast.success('Welcome to Galeries Yess!');
            navigate('/home');
        } catch (error) {
            toast.error('Failed to save. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex flex-col" style={{ backgroundColor: '#F8BBD6' }}>
            <div className="flex-1 flex flex-col items-center justify-center px-6 py-12">
                <div className="mb-6">
                    <img
                        src={LOGO_URL}
                        alt="Galeries Yess"
                        className="w-48 h-auto"
                        data-testid="auth-logo"
                    />
                </div>

                <h1 className="font-serif text-3xl md:text-4xl text-foreground mb-2 text-center tracking-tight" style={{ fontFamily: 'Georgia, Times New Roman, serif', fontWeight: 400 }}>
                    Women. Worth. Second Lives.
                </h1>
                
                <p className="font-serif text-base italic text-foreground/70 mb-8 text-center" style={{ fontFamily: 'Georgia, Times New Roman, serif' }}>
                    A curated charity auction salon
                </p>

                {/* Step 1: Login - Name, Phone, Code on ONE page */}
                {step === 1 && (
                    <form onSubmit={handleLogin} className="w-full max-w-sm space-y-4">
                        <div>
                            <label className="text-sm font-medium text-foreground/70 mb-1 block">
                                Name
                            </label>
                            <Input
                                type="text"
                                placeholder="Your name"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                className="h-12 text-base bg-white border-white rounded-xl"
                                data-testid="name-input"
                            />
                        </div>

                        <div>
                            <label className="text-sm font-medium text-foreground/70 mb-1 block">
                                Phone number
                            </label>
                            <Input
                                type="tel"
                                placeholder="+316"
                                value={phone}
                                onChange={(e) => setPhone(e.target.value)}
                                className="h-12 text-base bg-white border-white rounded-xl"
                                data-testid="phone-input"
                            />
                        </div>

                        <div>
                            <label className="text-sm font-medium text-foreground/70 mb-1 block">
                                Access code
                            </label>
                            <Input
                                type="text"
                                placeholder="Enter code"
                                value={code}
                                onChange={(e) => setCode(e.target.value.toUpperCase())}
                                className="h-12 text-base bg-white border-white rounded-xl uppercase tracking-wider"
                                data-testid="code-input"
                                maxLength={8}
                            />
                        </div>

                        <Button
                            type="submit"
                            disabled={loading || !phone || !name || !code}
                            className="w-full h-14 rounded-full text-lg font-heading bg-black hover:bg-black/80 text-white mt-6"
                            data-testid="login-button"
                        >
                            {loading ? (
                                <Loader2 className="w-5 h-5 animate-spin" />
                            ) : (
                                <>
                                    Login
                                    <ArrowRight className="ml-2 w-5 h-5" />
                                </>
                            )}
                        </Button>
                    </form>
                )}

                {/* Step 2: Two checkboxes - Terms AND House Rules */}
                {step === 2 && (
                    <form onSubmit={handleAcceptConsent} className="w-full max-w-sm">
                        <div className="bg-white/80 rounded-2xl p-6 mb-6">
                            <h2 className="font-heading text-lg font-medium mb-4 text-center">
                                Before you enter
                            </h2>
                            
                            {/* Checkbox 1: Terms */}
                            <div className="flex items-start gap-3 mb-4">
                                <Checkbox
                                    id="termsConsent"
                                    checked={termsChecked}
                                    onCheckedChange={setTermsChecked}
                                    className="mt-1 border-gray-400 data-[state=checked]:bg-black data-[state=checked]:border-black"
                                    data-testid="terms-checkbox"
                                />
                                <label htmlFor="termsConsent" className="text-sm text-foreground/80 leading-relaxed cursor-pointer">
                                    I agree to the terms and understand that photography and video may be taken during the event for social media and promotional purposes.
                                </label>
                            </div>

                            {/* Checkbox 2: House Rules */}
                            <div className="flex items-start gap-3">
                                <Checkbox
                                    id="houseRulesConsent"
                                    checked={houseRulesChecked}
                                    onCheckedChange={setHouseRulesChecked}
                                    className="mt-1 border-gray-400 data-[state=checked]:bg-black data-[state=checked]:border-black"
                                    data-testid="houserules-checkbox"
                                />
                                <label htmlFor="houseRulesConsent" className="text-sm text-foreground/80 leading-relaxed cursor-pointer">
                                    I agree to the{' '}
                                    <button 
                                        type="button"
                                        onClick={() => setShowHouseRules(true)}
                                        className="underline underline-offset-2 font-medium hover:text-black"
                                    >
                                        House Rules
                                    </button>
                                </label>
                            </div>
                        </div>

                        <Button
                            type="submit"
                            disabled={loading || !houseRulesChecked || !termsChecked}
                            className="w-full h-14 rounded-full text-lg font-heading bg-black hover:bg-black/80 text-white disabled:opacity-50"
                            data-testid="accept-consent-button"
                        >
                            {loading ? (
                                <Loader2 className="w-5 h-5 animate-spin" />
                            ) : (
                                <>
                                    Enter Galeries Yess
                                    <ArrowRight className="ml-2 w-5 h-5" />
                                </>
                            )}
                        </Button>
                    </form>
                )}

                {/* House Rules Modal */}
                <Dialog open={showHouseRules} onOpenChange={setShowHouseRules}>
                    <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
                        <DialogHeader>
                            <DialogTitle className="font-heading text-2xl">Huisregels</DialogTitle>
                        </DialogHeader>
                        
                        {houseRulesLoading ? (
                            <div className="space-y-4 mt-4">
                                <Skeleton className="h-6 w-48" />
                                <Skeleton className="h-16 w-full" />
                            </div>
                        ) : houseRulesContent ? (
                            <div className="space-y-6 text-gray-600 leading-relaxed mt-4">
                                {houseRulesContent.rules && houseRulesContent.rules.map((rule, index) => (
                                    <section key={index}>
                                        <h3 className="text-base font-heading font-medium text-black mb-2">
                                            {rule.title}
                                        </h3>
                                        <p className="text-sm whitespace-pre-line">
                                            {rule.content}
                                        </p>
                                    </section>
                                ))}
                            </div>
                        ) : (
                            <div className="text-center text-gray-500 mt-4">
                                Huisregels konden niet worden geladen
                            </div>
                        )}
                        
                        <div className="mt-6 pt-4 border-t border-gray-100">
                            <Button
                                onClick={() => setShowHouseRules(false)}
                                className="w-full bg-black text-white"
                            >
                                Sluiten
                            </Button>
                        </div>
                    </DialogContent>
                </Dialog>
            </div>

            <div className="pb-8 text-center">
                <p className="text-xs text-foreground/50">
                    By continuing, you agree to our terms
                </p>
            </div>
        </div>
    );
};

export default AccessPage;
